/**
 * Daily targets: calories and macros that always add up (4/4/9), set in grams, as percentages of
 * calories, or protein and fat first with carbs filling the rest.
 */
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { errorMessage } from '../../lib/logger';
import { KG_PER_LB, planFromTargets, planMacros, PROTEIN_BASIS_LABEL, withCalories, type MacroPlanResult } from '../../lib/macros';
import type { MacroMode, MacroPlan, ProteinBasis } from '../../lib/types';
import { useApp } from '../../state/app';
import { Icon } from '../Icon';
import { Card, Segmented, Spinner } from '../ui';

interface Form {
  mode: MacroMode;
  calories: string;
  protein_g: string; carbs_g: string; fat_g: string;
  protein_pct: string; carbs_pct: string; fat_pct: string;
  protein_amount: string; protein_basis: ProteinBasis;
  fat_amount: string; fat_basis: 'g' | 'pct';
}

const s = (v: number | undefined | null, digits: number = 1): string => (v === undefined || v === null ? '' : String(Math.round(v * 10 ** digits) / 10 ** digits));
const n = (v: string): number | undefined => { const t = v.trim(); if (t === '') return undefined; const x = Number(t); return Number.isFinite(x) ? x : Number.NaN; };

function formFrom(plan: MacroPlan): Form {
  return {
    mode: plan.mode, calories: s(plan.calories, 0),
    protein_g: s(plan.protein_g), carbs_g: s(plan.carbs_g), fat_g: s(plan.fat_g),
    protein_pct: s(plan.protein_pct), carbs_pct: s(plan.carbs_pct), fat_pct: s(plan.fat_pct),
    protein_amount: s(plan.protein_amount, 2), protein_basis: plan.protein_basis ?? 'g',
    fat_amount: s(plan.fat_amount), fat_basis: plan.fat_basis ?? 'g',
  };
}

function planFrom(f: Form): MacroPlan {
  const plan: MacroPlan = { mode: f.mode };
  const set = (k: keyof MacroPlan, v: string): void => { const x = n(v); if (x !== undefined) (plan as unknown as Record<string, number>)[k] = x; };
  if (f.mode === 'grams') { set('protein_g', f.protein_g); set('carbs_g', f.carbs_g); set('fat_g', f.fat_g); }
  if (f.mode === 'percent') { set('calories', f.calories); set('protein_pct', f.protein_pct); set('carbs_pct', f.carbs_pct); set('fat_pct', f.fat_pct); }
  if (f.mode === 'protein_fat_first') {
    set('calories', f.calories); set('protein_amount', f.protein_amount); set('fat_amount', f.fat_amount);
    plan.protein_basis = f.protein_basis; plan.fat_basis = f.fat_basis;
  }
  return plan;
}

const MODE_LABEL: Readonly<Record<MacroMode, string>> = { grams: 'Grams', percent: 'Percent', protein_fat_first: 'Protein & fat first' };
const MODE_HELP: Readonly<Record<MacroMode, string>> = {
  grams: 'Type protein, carbs and fat in grams. Your calories are whatever they add up to.',
  percent: 'Type your calories and how to split them. The three percentages must total 100%.',
  protein_fat_first: 'Set calories, protein and fat; carbs get what’s left. The way most lifters set up a cut or a bulk.',
};

export function MacroPlanner({ recommendedKcal }: { recommendedKcal: number | null }): ReactElement {
  const { source, profile, setProfile, settings, saveSettings, energy, toast, bump } = useApp();
  const start: MacroPlan = settings.macro_plan ?? planFromTargets(profile);
  const [f, setF] = useState<Form>(() => formFrom(start));
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // A plan saved elsewhere (another tab) replaces an untouched form.
  useEffect(() => { if (settings.macro_plan) setF(formFrom(settings.macro_plan)); }, [settings.macro_plan]);

  const body = { weightKg: energy.comp.weightKg, leanMassKg: energy.comp.leanMassKg };
  const result: MacroPlanResult = useMemo(() => planMacros(planFrom(f), body), [f, body.weightKg, body.leanMassKg]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = <K extends keyof Form>(k: K, v: Form[K]): void => setF((x) => ({ ...x, [k]: v }));

  /** Switching mode keeps the current numbers, just expressed the new way. */
  const switchMode = (mode: MacroMode): void => {
    if (!result.ok) { set('mode', mode); return; }
    const t = result.targets;
    const p: number = Math.round(result.shares.protein * 100);
    const fat: number = Math.round(result.shares.fat * 100);
    if (mode === 'grams') setF((x) => ({ ...x, mode, protein_g: s(t.protein_g), carbs_g: s(t.carbs_g), fat_g: s(t.fat_g) }));
    if (mode === 'percent') setF((x) => ({ ...x, mode, calories: String(t.calories), protein_pct: String(p), fat_pct: String(fat), carbs_pct: String(100 - p - fat) }));
    if (mode === 'protein_fat_first') setF((x) => ({ ...x, mode, calories: String(t.calories), protein_amount: s(t.protein_g), protein_basis: 'g', fat_amount: s(t.fat_g), fat_basis: 'g' }));
  };

  const applyCalories = (kcal: number): void => {
    const plan: MacroPlan = withCalories(planFrom(f), kcal, result.ok ? result.targets : null);
    setF(formFrom(plan));
  };

  const save = async (): Promise<void> => {
    setError(null);
    if (!result.ok) { setError(result.error); return; }
    setSaving(true);
    try {
      const t = result.targets;
      setProfile(await source.updateProfile({ target_calories: t.calories, target_protein_g: t.protein_g, target_carbs_g: t.carbs_g, target_fat_g: t.fat_g }));
      await saveSettings({ macro_plan: planFrom(f) });
      toast(`Targets saved: ${t.calories.toLocaleString()} kcal`);
      bump();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const pctTotal: number = (n(f.protein_pct) ?? 0) + (n(f.carbs_pct) ?? 0) + (n(f.fat_pct) ?? 0);
  const proteinG: number | null = result.ok ? result.targets.protein_g : null;
  const perKg = (g: number, kg: number | null): string => (kg !== null && kg > 0 ? (g / kg).toFixed(2) : '—');
  const num = (label: string, k: keyof Form, suffix: string, color?: string, hint?: string): ReactElement => (
    <label className="field"><span style={color ? { color } : undefined}>{label}</span>
      <div className="input-suffix"><input className="input" type="number" inputMode="decimal" min={0} step="any" value={f[k] as string} onChange={(e) => set(k, e.target.value as Form[typeof k])} /><em>{suffix}</em></div>
      {hint && <small>{hint}</small>}
    </label>
  );

  return (
    <Card title="Daily targets" sub="calories and macros always add up">
      <div className="stack" style={{ gap: 14 }}>
        <div>
          <Segmented<MacroMode> options={(['grams', 'percent', 'protein_fat_first'] as const).map((m) => ({ value: m, label: MODE_LABEL[m] }))} value={f.mode} onChange={switchMode} />
          <p className="muted" style={{ margin: '8px 0 0', fontSize: 13.5 }}>{MODE_HELP[f.mode]}</p>
        </div>

        {f.mode !== 'grams' && (
          <div className="stack" style={{ gap: 8 }}>
            {num('Calories', 'calories', 'kcal', 'var(--energy)')}
            <div className="row" style={{ gap: 8 }}>
              {recommendedKcal !== null && <button type="button" className="btn btn-sm" onClick={() => applyCalories(recommendedKcal)}><Icon name="target" size={15} /> Use my goal’s {recommendedKcal.toLocaleString()} kcal</button>}
              {energy.planning.kcal !== null && <button type="button" className="btn btn-sm btn-ghost" onClick={() => applyCalories(Math.round(energy.planning.kcal!))}>Match my typical burn ({Math.round(energy.planning.kcal).toLocaleString()})</button>}
            </div>
          </div>
        )}
        {f.mode === 'grams' && recommendedKcal !== null && (
          <button type="button" className="btn btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => applyCalories(recommendedKcal)}><Icon name="target" size={15} /> Rebuild around my goal’s {recommendedKcal.toLocaleString()} kcal (keeps protein and fat)</button>
        )}

        {f.mode === 'grams' && (
          <div className="form-grid">
            {num('Protein', 'protein_g', 'g', 'var(--protein)')}
            {num('Carbs (total)', 'carbs_g', 'g', 'var(--carbs)')}
            {num('Fat', 'fat_g', 'g', 'var(--fat)')}
          </div>
        )}

        {f.mode === 'percent' && (
          <>
            <div className="form-grid">
              {num('Protein', 'protein_pct', '%', 'var(--protein)')}
              {num('Carbs', 'carbs_pct', '%', 'var(--carbs)')}
              {num('Fat', 'fat_pct', '%', 'var(--fat)')}
            </div>
            <div className="row" style={{ gap: 8 }}>
              <span className={`num ${Math.abs(pctTotal - 100) <= 0.5 ? 'good' : 'warn'}`} style={{ fontWeight: 800, fontSize: 13.5 }}>Total {Math.round(pctTotal * 10) / 10}%</span>
              {Math.abs(pctTotal - 100) > 0.5 && (
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => set('carbs_pct', String(Math.max(0, Math.round((100 - (n(f.protein_pct) ?? 0) - (n(f.fat_pct) ?? 0)) * 10) / 10)))}>Let carbs fill the rest</button>
              )}
            </div>
          </>
        )}

        {f.mode === 'protein_fat_first' && (
          <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="field"><span style={{ color: 'var(--protein)' }}>Protein</span>
              <div className="input-row">
                <input className="input" type="number" inputMode="decimal" min={0} step="any" value={f.protein_amount} onChange={(e) => set('protein_amount', e.target.value)} aria-label="Protein amount" />
                <select className="input" value={f.protein_basis} onChange={(e) => set('protein_basis', e.target.value as ProteinBasis)} aria-label="Protein unit">
                  {(Object.keys(PROTEIN_BASIS_LABEL) as ProteinBasis[]).map((b) => <option key={b} value={b}>{PROTEIN_BASIS_LABEL[b]}</option>)}
                </select>
              </div>
              {proteinG !== null && <small>= {Math.round(proteinG)} g · {perKg(proteinG, body.weightKg)} g/kg body weight · {perKg(proteinG, body.leanMassKg)} g/kg lean mass</small>}
            </div>
            <div className="field"><span style={{ color: 'var(--fat)' }}>Fat</span>
              <div className="input-row">
                <input className="input" type="number" inputMode="decimal" min={0} step="any" value={f.fat_amount} onChange={(e) => set('fat_amount', e.target.value)} aria-label="Fat amount" />
                <select className="input" value={f.fat_basis} onChange={(e) => set('fat_basis', e.target.value as 'g' | 'pct')} aria-label="Fat unit">
                  <option value="g">grams a day</option><option value="pct">% of calories</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {result.ok ? (
          <div className="macro-result">
            <div className="macro-bar" role="img" aria-label="Calories by macro">
              <i style={{ width: `${result.shares.protein * 100}%`, background: 'var(--protein)' }} />
              <i style={{ width: `${result.shares.carbs * 100}%`, background: 'var(--carbs)' }} />
              <i style={{ width: `${result.shares.fat * 100}%`, background: 'var(--fat)' }} />
            </div>
            <div className="macro-sum num">
              <b>{result.targets.calories.toLocaleString()} kcal</b> =
              <span style={{ color: 'var(--protein)' }}> {result.targets.protein_g} g protein</span> ({Math.round(result.targets.protein_g * 4).toLocaleString()} kcal, {Math.round(result.shares.protein * 100)}%) +
              <span style={{ color: 'var(--carbs)' }}> {result.targets.carbs_g} g carbs</span> ({Math.round(result.targets.carbs_g * 4).toLocaleString()} kcal, {Math.round(result.shares.carbs * 100)}%) +
              <span style={{ color: 'var(--fat)' }}> {result.targets.fat_g} g fat</span> ({Math.round(result.targets.fat_g * 9).toLocaleString()} kcal, {Math.round(result.shares.fat * 100)}%)
            </div>
            {result.notes.map((x) => <div key={x} className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>{x}</div>)}
          </div>
        ) : (
          <div className="note warn">{result.error}</div>
        )}

        <details>
          <summary className="cite" style={{ cursor: 'pointer', fontWeight: 700 }}>How much protein?</summary>
          <ul className="cite" style={{ paddingLeft: 18, margin: '6px 0 0' }}>
            <li>Gains from protein level off around 1.6 g per kg of body weight a day on average, up to about 2.2 g/kg for some (Morton RW et al. Br J Sports Med 2018, PMID 28698222). {body.weightKg !== null ? `For you that’s ${Math.round(1.6 * body.weightKg)}-${Math.round(2.2 * body.weightKg)} g.` : ''}</li>
            <li>While cutting, lean lifters keep more muscle at 2.3-3.1 g per kg of lean mass (Helms ER et al. J Int Soc Sports Nutr 2014, PMID 24864135). {body.leanMassKg !== null ? `For you that’s ${Math.round(2.3 * body.leanMassKg)}-${Math.round(3.1 * body.leanMassKg)} g.` : ''}</li>
            <li>1 g per lb of lean mass = {(1 / KG_PER_LB).toFixed(2)} g per kg of lean mass.</li>
            <li>Reference ranges for adults: protein 10-35%, fat 20-35%, carbs 45-65% of calories (Institute of Medicine 2005).</li>
          </ul>
        </details>

        {error !== null && <div className="note err">{error}</div>}
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving || !result.ok}>{saving ? <Spinner /> : <Icon name="check" size={18} />} Save targets</button>
        </div>
      </div>
    </Card>
  );
}
